import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Icon, Loader } from "semantic-ui-react";

import { AppointmentPin, CopyToClipboard, DATE_FORMATS_FNS, ScrollToSectionMenu } from "components";
import { setShowPriceInVat, usePermanentRealTimeUser, useScrollPageToTop, useShowPriceInVat } from "hooks";
import { DMS_CAPABILITIES, STATUS_IDENTIFIERS } from "models";
import "modules/AppointmentDetails/AppointmentDetails.scss";
import { CopyContent } from "modules/AppointmentDetails/components";
import { useAppointmentLocation, useRealTimeAppointmentQuery, useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";
import { AppointmentStatus, CarDetails, Checks, IndicatorsAction, Interventions } from "modules/HoldingAppointmentDetails/components";
import { useScrollToSectionMenus } from "modules/HoldingAppointmentDetails/hooks";
import { useRedirectToLandingPage } from "modules/Login/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { RedirectProps } from "react-router-dom";

export const HoldingAppointmentDetails = () => {
  const { t } = useTranslation();
  const router = useHistory();
  const location = useLocation<RedirectProps | undefined>();

  const [isExpandedByDefaultExpanded, setIsExpandedByDefaultExpanded] = useState(true);
  const [isBackPressed, setIsBackPressed] = useState(false);

  const { appointment, loading, error } = useRealTimeAppointmentQuery();
  const { checks } = useRealTimeOrderedChecks(appointment?.id);
  const appointmentLocation = useAppointmentLocation(appointment);
  const { user } = usePermanentRealTimeUser();
  const redirectToLandingPage = useRedirectToLandingPage();
  const showVat = useShowPriceInVat();

  const scrollToSectionMenus = useScrollToSectionMenus(appointment, checks);

  useScrollPageToTop();

  useEffect(() => {
    if (!error) return;
    toast.error(t("v8_error_loading_appointment", "Something went wrong loading the appointment. Please try again"));
  }, [error]);

  useEffect(() => {
    if (appointmentLocation) setShowPriceInVat(Boolean(appointmentLocation.include_vat && user?.is_vat_enabled_on_wo));
  }, [appointmentLocation?.id]);

  useEffect(() => {
    return () => {
      setIsBackPressed(false);
    };
  }, []);

  const toggleExpand = () => setIsExpandedByDefaultExpanded(prev => !prev);
  const toggleShowVat = () => setShowPriceInVat(!showVat);
  const navigateBack = () => {
    setIsBackPressed(true);

    const from = location.state?.from;

    if (window.history.length > 1) from ? router.replace(from) : router.goBack();
    else redirectToLandingPage();
  };

  if (loading)
    return (
      <div className="AppointmentDetails-loader">
        <Loader active inline />
      </div>
    );

  if (!appointment) return;

  return (
    <div className={`AppointmentDetails ${appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus ? "CanceledAppointment" : ""}`}>
      <div className="AppointmentDetails-container">
        <div className="AppointmentDetails-NavigationHeader">
          <div className="AppointmentDetails-NavigationHeader-container">
            <div className="row">
              <AppointmentStatus appointment={appointment} />
              {appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus && (
                <IndicatorsAction appointment={appointment} showVat={showVat} onSwitchShowVat={toggleShowVat} />
              )}
            </div>
            <div className="row tabs-row-container">
              <ScrollToSectionMenu sectionMenus={scrollToSectionMenus} offset={-280} hideBorder />
              <div className="sub-row">
                {appointment.car?.reg_nr && (
                  <div className="Appointment-reg-nr">
                    <CopyToClipboard text={appointment.car.reg_nr} onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />
                  </div>
                )}
                <div className="Appointment-time">
                  <div>
                    <strong>{t("v8_appointment", "Appointment")}</strong>
                    <span>{formatDate(appointment.time_car_app, DATE_FORMATS_FNS.dateMonthYear)}</span>
                  </div>

                  {(appointment.is_local || appointmentLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.ScheduledInOut)) && (
                    <>
                      {appointment.due_in && (
                        <div>
                          <strong>{t("v8_in", "In")}</strong>
                          {appointment.due_in && formatDate(appointment.due_in, DATE_FORMATS_FNS.dateMonthYearTime)}
                        </div>
                      )}

                      {appointment.car_return_time && (
                        <div>
                          <strong>{t("v8_out", "Out")}</strong>
                          {appointment.car_return_time && formatDate(appointment.car_return_time, DATE_FORMATS_FNS.dateMonthYearTime)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="Appointment-time">
                  <CopyContent content={appointment.wo_nr}>
                    <div>
                      <strong>{t("v8_wo", "WO")} #</strong>
                      <span className={appointment.internal ? "info-wo" : ""}>{appointment.wo_nr}</span>
                      <span className="appointment-pin ml-5">
                        <AppointmentPin appointment={appointment} location={appointmentLocation} readOnly />
                      </span>
                    </div>
                  </CopyContent>
                </div>
                <Button className="-appointment-status icon" size="small" onClick={toggleExpand}>
                  <Icon className={`arrows ${isExpandedByDefaultExpanded ? "minimize" : "maximize"}`} color="green" />
                </Button>
                <Button className="-appointment-status" size="small" active loading={isBackPressed} disabled={isBackPressed} onClick={navigateBack}>
                  <Icon className="angle left" />
                  {t("v8_back", "Back")}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="AppointmentDetails-cards mt-20">
          <CarDetails sectionMenu={scrollToSectionMenus.details} appointment={appointment} isExpanded={isExpandedByDefaultExpanded} />
          {appointmentLocation && (
            <Interventions
              sectionMenu={scrollToSectionMenus.interventions}
              isExpanded={isExpandedByDefaultExpanded}
              appointment={appointment}
              appointmentLocation={appointmentLocation}
            />
          )}
          <Checks sectionMenu={scrollToSectionMenus.checks} isExpanded={isExpandedByDefaultExpanded} appointment={appointment} checks={checks} />
        </div>
      </div>
    </div>
  );
};
